import DishServ from '@api/Dishes/DishServ';
import RecipeServ from '@api/Recipes/RecipeServ';
import ModifierGroupServ from '@api/ModifierGroups/ModifierGroupServ';
import InputServ from '@api/Inputs/InputServ';
import ProductCategoryServ from '@api/ProductCategories/ProductCategoryServ';
import { Dish } from '@api/Dishes/Dish';
import { Recipe } from '@api/Recipes/Recipe';
import { ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { Input } from '@api/Inputs/Input';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import { CatalogCacheEntry, isCatalogCacheFresh } from './CatalogVersion';
import { readCatalogSnapshot } from './CatalogSnapshot';

// ============================================================================
// Catálogo del POS: **1 lectura por montaje**, siempre.
//
// Historia corta, porque importa para no repetirla:
// ① El POS leía las 5 colecciones completas (~336 docs) en CADA montaje — cada
//    vuelta del tablero de cocina al POS.
// ② El 7-sep se le puso una caché de módulo con versión: bajaba a 1 lectura
//    **si la pestaña seguía viva**. Medido el 8-sep: no bastó. La caché muere en
//    cada recarga, y las tablets recargan solas con cada deploy
//    (`useAppUpdateChecker`), así que en frío seguía costando 464 docs (336 del
//    catálogo + el N+1 de categorías de insumo, ya corregido).
// ③ Hoy lee `catalog/snapshot`: el catálogo YA ARMADO en un documento. **1
//    lectura**, viva o no la pestaña. La caché de módulo se queda para no
//    re-transferir los 171 KB dentro de la misma pestaña.
//
// Sin snapshot usable cae al camino de ② (336 lecturas) — decisión D3 del plan:
// el snapshot nunca es la única verdad, las colecciones siguen siendo la fuente.
//
// Los CRUDs del catálogo NO pasan por aquí (editan y releen lo suyo).
// ============================================================================

export interface PosCatalog {
    dishes: Dish[];
    recipes: Recipe[];
    modifierGroups: ModifierGroup[];
    inputs: Input[];
    productCategories: ProductCategory[];
}

let cache: CatalogCacheEntry<PosCatalog> | undefined;
// Deduplicación: dos montajes casi simultáneos con la caché vencida = UNA carga.
let inflight: Promise<PosCatalog> | undefined;

const loadPosCatalog = async (): Promise<PosCatalog> => {
    const [dishes, recipes, modifierGroups, inputs, productCategories] = await Promise.all([
        DishServ.getDishes(),
        RecipeServ.getRecipes(),
        ModifierGroupServ.getModifierGroups(),
        InputServ.getInputs(),
        ProductCategoryServ.getProductCategories()
    ]);
    return { dishes, recipes, modifierGroups, inputs, productCategories };
};

/** Catálogo para el POS: **1 lectura** (el snapshot publicado). */
export const getPosCatalog = async (): Promise<PosCatalog> => {
    const snapshot = await readCatalogSnapshot();
    if (snapshot) {
        // Misma versión que lo cacheado: se reusa y no se re-transfiere.
        if (cache && cache.version === snapshot.version) {
            return cache.value;
        }
        const catalog: PosCatalog = {
            dishes: snapshot.dishes,
            recipes: snapshot.recipes,
            modifierGroups: snapshot.modifierGroups,
            inputs: snapshot.inputs,
            productCategories: snapshot.productCategories
        };
        cache = { value: catalog, version: snapshot.version, loadedAt: Date.now() };
        return catalog;
    }
    // Fallback (D3): sin snapshot publicado o con uno inservible, el camino de
    // siempre — 336 lecturas, pero el POS ABRE. Es el estado de una instalación
    // nueva antes del primer guardado de catálogo.
    console.warn('Sin catálogo publicado: leyendo las colecciones (336 lecturas)');
    // ⚠️ Por este camino la caché SÍ caduca por tiempo. La primera versión
    // (2026-09-09) devolvía `cache.value` sin condición, así que una pestaña del
    // POS que hubiera cargado una vez **no volvía a leer mientras viviera** — y
    // las tablets viven todo el día: un cambio de precio o un platillo
    // desactivado no habría llegado nunca a esa caja.
    if (cache && isCatalogCacheFresh(cache, null, Date.now())) {
        return cache.value;
    }
    if (inflight) {
        return inflight;
    }
    inflight = loadPosCatalog()
        .then((catalog) => {
            cache = { value: catalog, version: null, loadedAt: Date.now() };
            return catalog;
        })
        .finally(() => {
            inflight = undefined;
        });
    return inflight;
};

/** Olvida lo cacheado (el próximo `getPosCatalog` recarga). */
export const invalidatePosCatalog = (): void => {
    cache = undefined;
};
